import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyMoneyMindDashboard } from './companyMoneyMindApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X
} from '../../../../component/common/designSystem';
import { TAG_RAMP, LAG_RAMP, VOLUME_RAMP } from './moneyMindTheme';

/**
 * MoneyMind → Dashboard.
 *
 * ── THE THING TO KNOW BEFORE READING THIS PAGE ──────────────────────────────
 * There is no money on it. Every entry your people log has a price and a
 * currency, and this section shows neither, at company level or per person.
 * What somebody spends their own money on is not an employer metric at any
 * level of aggregation; this section has a department filter, so an "average
 * monthly outgoing" over four people in one team is four people's finances; and
 * entries mix seven currencies, so a total would be meaningless anyway. The
 * separate income-and-savings collection is never touched.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * What is left is worth having, because MoneyMind is a reflection exercise
 * rather than a budgeting tool, and three things carry the page:
 *
 *  1. THE KEEP / MAYBE / LET GO MIX. The exercise itself, and categorical
 *     rather than financial. A workforce sorting most of what it logs into
 *     "can't live without it" is in a different place from one steadily
 *     letting things go.
 *  2. HABIT, NOT VOLUME. Thirty entries written on one afternoon is doing the
 *     exercise once. Distinct active days is the only measure that separates a
 *     habit from a single sitting, so it gets a card rather than an average.
 *  3. WHEN THEY LOG IT. Same day versus a week later says whether people are
 *     using this in the moment or catching up, which is the closest thing to a
 *     depth signal the feature produces.
 *
 * `fetcher` is a prop so a super-admin MoneyMind hub can render this page
 * unscoped; the builders behind both are the same.
 */
const MoneyMindDashboard = ({
  fetcher = fetchCompanyMoneyMindDashboard,
  populationLabel = 'users'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const tagMix = stats?.tagMix || [];
  const volumeSplit = stats?.volumeSplit || [];
  const logLagSplit = stats?.logLagSplit || [];
  const currencyMix = stats?.currencyMix || [];
  const weekdayEntries = stats?.weekdayEntries || [];
  const entriesTrend = stats?.entriesTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  // What share of the population has ever logged anything.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.users / stats.totalUsers) * 100) : null;

  const trendHasData = entriesTrend.some((val) => val > 0);

  /**
   * The keep / maybe / let-go mix as one bar. Proportions rather than counts,
   * because the interesting thing is the balance, not how much was logged.
   */
  const tagOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true, stackType: '100%' },
    colors: TAG_RAMP,
    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID_NO_X,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: {
      categories: ['Everything logged'],
      labels: { formatter: (val) => `${Math.round(val)}%` }
    },
    tooltip: { shared: true, y: { formatter: (val) => `${val.toLocaleString()} entries` } }
  };

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(entriesTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayEntries.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} entries` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline. Counts and days — never an amount. ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Using It"
          value={stats ? formatBigNumber(stats.users) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Things Logged"
          value={stats ? formatBigNumber(stats.entries) : '-'}
        />
        {/* The habit card. Separates a daily practice from one long sitting. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Logged Each"
          value={stats ? stats.avgActiveDays : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Came Back Again"
          value={stats?.users > 0 ? `${stats.returnRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Entries per Person"
          value={stats ? stats.avgEntriesPerUser : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Added a Note"
          value={stats?.entries > 0 ? `${stats.noteRate}%` : '-'}
        />
      </div>

      {/* ---------- Shape of use ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How They Sort It"
          subtitle="Keep, unsure, or let it go"
          data={tagMix}
          colors={TAG_RAMP}
          unit="entries"
          totalLabel="Entries"
          legendLimit={4}
        />
        <MiniDonutCard
          title="When They Log It"
          subtitle="After the spending itself"
          data={logLagSplit}
          colors={LAG_RAMP}
          unit="entries"
          totalLabel="Entries"
          legendLimit={4}
        />
        <MiniDonutCard
          title="How Much They Log"
          subtitle="Entries per person"
          data={volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={4}
        />
      </div>

      {/* ---------- Trend and the exercise itself ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Logging Over Time" subtitle="Entries written — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Entries', type: 'column', data: entriesTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing logged in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel
          title="Keep, Maybe, Let Go"
          subtitle="How your people sort what they spend on — no amounts"
        >
          {stats?.entries > 0 ? (
            <>
              <Chart
                type="bar"
                height={180}
                width="100%"
                series={tagMix.map((row) => ({ name: row.label, data: [row.count] }))}
                options={tagOptions}
              />
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Sorted as</th>
                    <th className="py-2 px-4 font-medium text-center">Entries</th>
                    <th className="py-2 font-medium text-right">Share</th>
                  </>
                }
              >
                {tagMix.map((row) => (
                  <tr key={row.label} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">{row.label}</td>
                    <td className="py-2 px-4 text-center">{formatBigNumber(row.count)}</td>
                    <td className="py-2 text-right">{row.share}%</td>
                  </tr>
                ))}
              </PanelTable>
            </>
          ) : (
            <PanelEmpty>Nobody has logged anything yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach and supporting detail ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="MoneyMind Reach"
          subtitle={`Share of ${populationLabel} who have used it`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.users)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Logging by Weekday" subtitle="When people sit down with it">
          {weekdayEntries.some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Entries', data: weekdayEntries.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>

        {/* A count of entries per currency, which for a company with people in
            several countries says something about who is using the feature. It
            is not a total in any currency, and there isn't one anywhere. */}
        <Panel title="Where They Are" subtitle="Entries by currency — a count, not a total">
          {currencyMix.length > 0 ? (
            <ProgressList items={currencyMix} unit="entries" color={CHART_COLORS.brand} />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Summary ---------- */}
      <div className="mt-6">
        <Panel title="MoneyMind Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">People who came back a second day</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.returningUsers) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Entries with a reflective note</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.entriesWithNote) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First entry logged</td>
              <td className="py-2 text-right">{stats?.firstEntryDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last entry logged</td>
              <td className="py-2 text-right">{stats?.lastEntryDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

MoneyMindDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default MoneyMindDashboard;
